import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { VETTED_QUERY } from '../../services/talentCommunityApi';
import { FIELDS, OPS, filterPeople, searchWords, type Rule } from './searchFilter';
import { Card, EmptyState, GroupTag } from './ui';

/**
 * Talent Search, as mockup v0.5 lays it out: a plain-words query, a rule-based filter builder, the
 * groups to search, and the result list a recruiter selects people from into a draft campaign.
 * Vetted Talent come from the Lever import (Oct 2026); Leads are not listed yet. The search is a
 * plain word match over name, role, stage, location and CV positions, sorted by NAME: NO match
 * score or ranking (docs/claude/talent-pool.md, EU AI Act Annex III).
 */
const COLUMNS = ['Candidate', 'Role & stage', 'Consent', 'Last contacted', 'Campaigns'];

let nextId = 1;
const newRule = (): Rule => ({ id: nextId++, field: 'Location', op: 'Contains', value: '' });

const month = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

export function SearchTab() {
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState('');
  const [vetted, setVetted] = useState(true);
  const [leads, setLeads] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Rule[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);

  const vettedQuery = useQuery(VETTED_QUERY);
  const all = useMemo(() => (vetted ? vettedQuery.data || [] : []), [vetted, vettedQuery.data]);
  const shown = useMemo(() => filterPeople(all, searchWords(searched), rules), [all, searched, rules]);

  const update = (id: number, patch: Partial<Rule>) => setDraft((d) => d.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  // The locked "Consent is Active" rule always counts: a lapsed consent never surfaces.
  const ruleCount = 1 + rules.length;

  return (
    <div className="space-y-5">
      <Card title="Find candidates">
        <form
          className="mb-3 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSearched(query.trim());
          }}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, role, company or skill, e.g. backend engineer fintech"
            aria-label="Search the Talent Community"
            className="min-w-[260px] flex-1 rounded border border-solid border-zinc-300 px-3 py-2 text-sm"
          />
          <Button type="submit" size="sm">
            Search
          </Button>
        </form>

        <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span className="text-zinc-500">Search in</span>
          <Checkbox checked={vetted} onCheckedChange={(v) => setVetted(v === true)} label={<GroupTag group="vetted" />} aria-label="Vetted Talent" />
          <Checkbox checked={leads} onCheckedChange={(v) => setLeads(v === true)} label={<GroupTag group="leads" />} aria-label="Leads" />
          <Checkbox checked={false} disabled label={<span className="flex items-center gap-2"><GroupTag group="internal" /> <span className="text-xs text-zinc-400">not in version 1</span></span>} aria-label="Internal Talents" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setDraft(rules.length ? rules.map((r) => ({ ...r })) : [newRule()]);
              setEditing(!editing);
            }}
            className="rounded border border-solid border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 hover:bg-zinc-50"
            aria-expanded={editing}
          >
            Filtered by <b>{ruleCount}</b> {ruleCount === 1 ? 'rule' : 'rules'}
          </button>
          {rules.length > 0 && (
            <button type="button" onClick={() => setRules([])} className="text-sm text-zinc-500 underline">
              Clear filters
            </button>
          )}
          <span className="text-sm text-zinc-500">
            <b className="text-zinc-900">{shown.length}</b> of {vettedQuery.data?.length ?? 0} eligible
          </span>
        </div>

        {editing && (
          <div className="mt-3 space-y-2 rounded border border-solid border-zinc-200 bg-zinc-50 p-3">
            <div className="grid grid-cols-[1fr_1fr_2fr_auto] items-center gap-2 text-sm text-zinc-500">
              <span className="rounded border border-solid border-zinc-200 bg-white px-3 py-2">Consent</span>
              <span className="rounded border border-solid border-zinc-200 bg-white px-3 py-2">Is</span>
              <span className="rounded border border-solid border-zinc-200 bg-white px-3 py-2">Active</span>
              <span className="w-16 text-xs">Always on</span>
            </div>
            {draft.map((r) => (
              <div key={r.id} className="grid grid-cols-[1fr_1fr_2fr_auto] items-center gap-2">
                <Select value={r.field} onValueChange={(v: string) => update(r.id, { field: v as Rule['field'] })}>
                  <SelectTrigger aria-label="Field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELDS.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={r.op} onValueChange={(v: string) => update(r.id, { op: v as Rule['op'] })}>
                  <SelectTrigger aria-label="Condition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  value={r.value}
                  onChange={(e) => update(r.id, { value: e.target.value })}
                  placeholder="Value"
                  aria-label="Value"
                  className="rounded border border-solid border-zinc-300 bg-white px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setDraft((d) => d.filter((x) => x.id !== r.id))}
                  className="w-16 text-sm text-zinc-500 hover:text-red-700"
                  aria-label="Remove rule"
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setDraft((d) => [...d, newRule()])} className="text-sm font-medium text-blue-700">
              + Add rule
            </button>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => {
                  setRules(draft.filter((r) => r.value.trim()));
                  setEditing(false);
                }}
              >
                Apply filters
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Results">
        {shown.length > 0 ? (
          <div className="overflow-x-auto rounded border border-solid border-zinc-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c} className="px-3 py-2 font-medium">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} className="border-t border-solid border-zinc-100 align-top">
                    <td className="border-l-4 border-solid border-l-zinc-900 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {p.leverUrl ? (
                          <a href={p.leverUrl} target="_blank" rel="noreferrer" className="font-medium text-zinc-900 underline-offset-2 hover:underline">
                            {p.name}
                          </a>
                        ) : (
                          <span className="font-medium text-zinc-900">{p.name}</span>
                        )}
                        <GroupTag group="vetted" />
                        {p.inProcess && (
                          <span className="rounded border border-solid border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">In a Lever process</span>
                        )}
                      </div>
                      {p.cvLines.length > 0 && <p className="mt-1 text-xs text-zinc-500">{p.cvLines.slice(0, 2).join(' — ')}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-zinc-900">{[p.role, p.location].filter(Boolean).join(' · ') || '—'}</p>
                      <p className="text-xs text-zinc-500">
                        {p.stage ? `Reached ${p.stage}` : 'Stage unknown'} · archived {month(p.archivedAt)}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-zinc-900">{p.news ? 'Scope A + B' : 'Scope A only'}</p>
                      <p className="text-xs text-zinc-500">until {month(p.consentUntil)}</p>
                    </td>
                    <td className="px-3 py-2 text-zinc-500">Not yet contacted</td>
                    <td className="px-3 py-2 text-zinc-500">In 0 campaigns</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title={
              vettedQuery.isLoading
                ? 'Loading…'
                : vettedQuery.isError
                  ? 'Vetted Talent could not be loaded'
                  : searched || rules.length
                    ? 'Nobody matches this search'
                    : 'No candidates yet'
            }
          >
            {!vetted && !leads
              ? 'Pick at least one group to search in.'
              : vettedQuery.isError
                ? 'Reload the page. If it keeps failing, the Lever import may not have run yet.'
                : 'Vetted Talent appear here after the Lever import (Settings › Lever import). Leads are listed once their sign-ups are connected.'}
          </EmptyState>
        )}
        <p className="mt-3 text-xs text-zinc-500">
          Sorted by name. Matches any of your search words in name, role, stage, location and CV — no score or ranking.
          Names open the Lever profile, where the interview notes and scorecards are.
        </p>
      </Card>
    </div>
  );
}
